import { createLoader, removeLoader } from "helpers/loader/loader";

/*************************** Modal config ******************************/
/*
    The modal can be instantiated like this:

    const myModal = new Modal({
        selector: '.modal',                 (optional| string) : The selector of the modal content
        content: '',                        (optional | string/obj) : the modal content, this can be a string that represents html, or a dom element
        isClosable: true,                   (required | boolean) : Make the modal closable or not
        onModalOpen: function (modal) {},   (optional | function) : Callback function that is fired when the modal is opened
        onModalClose: function (modal) {}   (optional | function) : Callback function that is fired when the modal is closed
    });

    Methods that can be used:

    myModal.show();                         Makes the modal visible
    myModal.hide();                         Makes the modal invisible
    myModal.destroy();                      For now this is the same as hide, in the future this might change.

    Or like this:

    <a href="#" data-modal-target=".class-of-modal-content">This is the modal opener</a>
 */

export const Modal = function (config) {
  /*********** Store the config that is passed from outside ***********/
  this.config = config;
  this.config.isOpen = false;

  /*********** If there is no selector and no content don't do anything ***********/
  if (!this.config.selector && !this.config.content) {
    return;
  }

  /*********** Set initial variables ***********/
  this.config.selector = this.config.selector ? this.config.selector : ".modal";
  this.config.body = document.querySelector("body");
  this.config.wrapperClass = "modal-wrapper";
  this.state = "closed";

  /*********** Create the modal wrapper ***********/
  this.config.modalWrapper = document.createElement("div");
  this.config.modalWrapper.classList.add(this.config.wrapperClass);

  /*********** Event listeners ***********/
  const self = this;
  this.config.modalWrapper.addEventListener("click", function (e) {
    self.handleEvents(e);
  });

  /*********** add to modal collection list ***********/
  this.modalCollection.push(this);
};

/*********** Handle events, example: click on the close button ***********/
Modal.prototype.handleEvents = function (e) {
  if (this.config.isClosable) {
    const closeButton = e.target.closest("[data-modal-close=true]");

    // handle click on the overlay (this actually checks if there has been a click event on the wrapper instead).
    if (e.target.classList.contains(this.config.wrapperClass)) {
      this.hide();
    }

    if (closeButton) {
      e.preventDefault(); // disable the default behavior
      this.hide();
    }
  }
};

/*********** Show the modal ***********/
Modal.prototype.show = function () {
  this.config.isOpen = true;
  // @TODO: Issue is here, state is set to 'open' but modal is not yet present,
  // @TODO: so all modals are hidden before the last one is shown
  //Check for existing modals and close them if open.
  if (this.modalCollection.length) {
    this.modalCollection.forEach((modal) => {
      if (modal.state === "open") {
        modal.hide();
      }
    });
  }

  createLoader(this.config.modalWrapper, 50); // show the loader icon

  this.config.modal = document.querySelector(this.config.selector); // get the modal content
  this.state = "open";
  this.showOverlay(); // create the overlay
  this.showModal(); // create the modal
  this.setStyles(); // hide the scrollbar

  removeLoader(this.config.modalWrapper); // remove the loader icon

  // if a callback is given, execute it
  if (typeof this.config.onModalOpen === "function") {
    let modal = null;
    if (this.config.modal) {
      modal = this.config.modal;
    } else {
      modal = this.config.modalWrapper;
    }
    this.config.onModalOpen(modal);
  }
};

/*********** Hide the modal ***********/
Modal.prototype.hide = function () {
  this.config.isOpen = false;
  this.removeStyles(); // show the scrollbar

  if (!this.config.content) {
    this.restorePosition();
  }

  const self = this;
  window.setTimeout(function () {
    // wait till the fadeout is done
    self.hideOverlay(); // remove the overlay
    self.hideModal(); // remove the modal-content
  }, 250);

  this.state = "closed";

  // if a callback is given, execute it
  if (typeof this.config.onModalClose === "function") {
    let modal = null;
    if (this.config.modal) {
      modal = this.config.modal;
    } else {
      modal = this.config.modalWrapper;
    }
    this.config.onModalClose(modal);
  }
};

/*********** Set needed styles ***********/
Modal.prototype.setStyles = function () {
  this.config.body.classList.add("modal-shown");
};
/*********** Remove needed styles ***********/
Modal.prototype.removeStyles = function () {
  this.config.body.classList.remove("modal-shown");
};

/*********** Restore position ***********/
Modal.prototype.restorePosition = function () {
  const modalContent = this.config.modal;
  modalContent.classList.add("d-none");
  this.config.body.insertAdjacentElement("beforeend", modalContent);
};

/*********** Show the overlay ***********/
Modal.prototype.showOverlay = function () {
  this.config.overlay = document.createElement("div");
  this.config.overlay.classList.add("modal-overlay");
  this.config.body.insertAdjacentElement("beforeend", this.config.overlay);
};

/*********** Hide the overlay ***********/
Modal.prototype.hideOverlay = function () {
  this.config.overlay.remove();
};
/*********** Show the modal ***********/
Modal.prototype.showModal = function () {
  // create the modal wrapper
  // insert the wrapper in the html before the overlay
  // clone the modalcontent
  // insert the modalcontent clone in the modalwrapper element
  this.config.modalWrapper.innerHTML = "";
  this.config.overlay.insertAdjacentElement(
    "beforebegin",
    this.config.modalWrapper,
  );

  let modalContent = null;

  if (this.config.content) {
    // content property is set, insert this property as modal content
    modalContent = this.config.content;

    if (typeof modalContent === "string") {
      this.config.modalWrapper.insertAdjacentHTML("beforeend", modalContent);
    } else {
      this.config.modalWrapper.insertAdjacentElement("beforeend", modalContent);
    }
  } else {
    // content property is not set, clone modal and insert as modal content
    if (this.config.modal) {
      modalContent = this.config.modal;
      modalContent.classList.remove("d-none");
      this.config.modalWrapper.insertAdjacentElement(
        "beforeend",
        this.config.modal,
      );
    }
  }
};
/*********** Hide the modal ***********/
Modal.prototype.hideModal = function () {
  // modalwrapper is wrapped around the modal content,
  // therefore modalcontent doesn't need to be removed
  this.config.modalWrapper.remove();
};
/*********** Destroy everything ***********/
Modal.prototype.destroy = function () {
  this.hide();
};
/*********** Get the modal state (open / closed) ***********/
Modal.prototype.isOpen = function () {
  return this.config.isOpen;
};

/*********** Modal Collection | array with all the created modals on the page ***********/
Modal.prototype.modalCollection = []; //Collects all the created modal on the page

/*********** Bind the modal automatic to elements ***********/
// Function that finds all elements on the page that have data-modal-target defined
// and initializes the modal for these elements.
export const showModal = (function () {
  const init = function () {
    document.addEventListener("click", handleModals); // Listen to clicks on the page
  };

  const handleModals = function (e) {
    const modal = e.target.closest("[data-modal-target]"); // check if the clicked element is a modal

    if (modal) {
      // Open the modal
      const selector = modal.getAttribute("data-modal-target");
      if (selector) {
        e.preventDefault();
        const m = new Modal({
          selector: selector,
          isClosable: true,
        });
        m.show();
      }
    }
  };

  return {
    init: init,
  };
})();
